/**
 * The two signals a warehouse actually notices (US-33.2): a short tone and a buzz.
 *
 * <p>No audio file. A hundred and twenty milliseconds of a sine wave is four lines of Web Audio, and
 * a file would be one more thing the service worker has to cache and the roastery has to download.
 *
 * <p>Both are best-effort by design. A browser that has not had a gesture yet refuses to start an
 * audio context, a desktop has no vibration motor, and neither is a reason for a scan to fail — the
 * result is on the screen either way.
 */

/** Created on the first beep, not on import: an AudioContext made before a gesture starts suspended. */
let context: AudioContext | undefined;

function tone(frequency: number, seconds: number) {
  try {
    context ??= new AudioContext();
    if (context.state === "suspended") {
      void context.resume();
    }
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = frequency;
    // A square-edged tone clicks; a short ramp at each end is what makes it sound deliberate.
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.25, context.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + seconds);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + seconds);
  } catch {
    // No audio on this device or in this state; the screen still says what happened.
  }
}

function buzz(pattern: number | number[]) {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // Same: a missing motor is not an error.
  }
}

/** Recognised: one short, high note, the sound every handheld scanner in the world makes. */
export function beepFound() {
  tone(1760, 0.12);
  buzz(40);
}

/** Wrong: two low notes and a longer buzz, so a packer knows without looking up. */
export function beepRejected() {
  tone(220, 0.18);
  window.setTimeout(() => tone(196, 0.22), 190);
  buzz([80, 60, 160]);
}
